// Entrar com digital no app de celular — /autenticacao/digital, /minhas-notificacoes/celular/digital e o Admin
// (/configuracoes/app-celular/digitais). Ver BiometricLoginCommands.cs.
import { WOOTDASH_BASE, wootdashAuthHeaders } from '@/api/wootdash'
import type { WootDashSession } from '@/services/auth'

export interface BiometricLoginItem {
  id: string
  agentId: string
  agentName: string
  agentEmail: string | null
  device: string | null
  createdAt: string
  expiresAt: string
  lastUsedAt: string | null
}

function json(token: string) {
  return { ...wootdashAuthHeaders(token), 'Content-Type': 'application/json' }
}

/** Ativa no celular do agente logado: devolve o token de entrada (só aqui, uma vez) ou o problema. */
export async function enableBiometricLogin(authToken: string, device: string | null): Promise<{ id: string; token: string } | { problem: string }> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/minhas-notificacoes/celular/digital`, { method: 'POST', headers: json(authToken), body: JSON.stringify({ device }) })
    const data = await res.json().catch(() => null)
    if (res.ok && data?.token && data?.id) return { id: data.id, token: data.token }
    return { problem: data?.problem ?? `Erro ${res.status} ao ativar a digital.` }
  } catch {
    return { problem: 'Não foi possível falar com o servidor.' }
  }
}

/** Troca o token que a digital destravou por uma sessão normal. problem = recusado (venceu, senha mudou, desligada…). */
export async function signInWithBiometric(token: string, device: string | null): Promise<WootDashSession | { problem: string; refused: boolean }> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/autenticacao/digital`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, device }) })
    const data = await res.json().catch(() => null)
    if (res.ok && data?.token) {
      return { token: data.token, expiresAt: data.expiresAt, agentId: data.agentId, firstName: data.firstName, lastName: data.lastName, chatwootId: data.chatwootId ?? null, email: data.email ?? null }
    }
    return { problem: data?.problem ?? `Erro ${res.status} ao entrar com a digital.`, refused: res.status === 401 }
  } catch {
    return { problem: 'Sem conexão com o servidor. Tente de novo.', refused: false }
  }
}

export async function listMyBiometricLogins(authToken: string): Promise<BiometricLoginItem[]> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/minhas-notificacoes/celular/digital`, { headers: wootdashAuthHeaders(authToken) })
    return res.ok ? await res.json() : []
  } catch {
    return []
  }
}

/** Desliga uma das suas (Meu perfil, ou o próprio app ao desligar). */
export async function revokeMyBiometricLogin(authToken: string, id: string): Promise<boolean> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/minhas-notificacoes/celular/digital/${encodeURIComponent(id)}`, { method: 'DELETE', headers: wootdashAuthHeaders(authToken) })
    return res.ok || res.status === 404
  } catch {
    return false
  }
}

// ── Admin › Configurações Gerais › App de celular (settings.manage) ───────────────────────────────────

export async function listAllBiometricLogins(authToken: string): Promise<{ data: BiometricLoginItem[] | null; problem: string | null }> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/app-celular/digitais`, { headers: wootdashAuthHeaders(authToken) })
    if (res.status === 401 || res.status === 403) return { data: null, problem: 'Sem permissão pra ver as entradas com digital.' }
    return res.ok ? { data: await res.json(), problem: null } : { data: null, problem: `Erro ${res.status} no servidor.` }
  } catch {
    return { data: null, problem: 'Não foi possível falar com o servidor.' }
  }
}

export async function revokeBiometricLoginAdmin(authToken: string, id: string): Promise<boolean> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/app-celular/digitais/${encodeURIComponent(id)}`, { method: 'DELETE', headers: wootdashAuthHeaders(authToken) })
    return res.ok
  } catch {
    return false
  }
}
